import { useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Play,
  Sparkles,
  Target,
} from "lucide-react";

/* "See how Abhyas works." — major storytelling section.
   Refined typography-driven selector (Practice | Learn | AI) with an active
   underline indicator; each state is a light, cropped product glimpse. */

const STATES = [
  { id: "practice", label: "Practice", testId: "how-works-tab-practice" },
  { id: "learn", label: "Learn", testId: "how-works-tab-learn" },
  { id: "ai", label: "AI", testId: "how-works-tab-ai" },
];

const stateHeadline =
  "mx-auto max-w-2xl font-display text-[26px] font-bold leading-[1.2] tracking-tight text-[#17201A] sm:text-[34px] lg:text-[40px]";
const stateCopy =
  "mx-auto mt-4 max-w-2xl font-body text-[17px] leading-relaxed text-[#647067] sm:text-[19px]";
const frameClass =
  "relative mx-auto mt-8 max-w-3xl overflow-hidden rounded-2xl border border-[#14532D]/12 bg-white text-left shadow-[0_12px_40px_-10px_rgba(20,83,45,0.08)]";

const CropFade = () => (
  <div
    aria-hidden="true"
    className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white to-transparent"
  />
);

/* ---------- PRACTICE state — cropped question glimpse ---------- */
const PracticeVisual = () => (
  <div data-testid="how-works-visual-practice" className={`${frameClass} p-6 sm:p-8`}>
    <div className="flex flex-wrap items-center justify-between gap-2 text-xs font-bold text-[#647067] sm:text-sm">
      <p>
        Class 10 Mathematics <span className="mx-1 text-[#94A39B]">·</span>{" "}
        <span className="text-[#17201A]">Quadratic Equations</span>
      </p>
      <p>5-question session</p>
    </div>
    <div className="mt-3 flex gap-1.5">
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className={`h-1.5 w-8 rounded-full ${
            i < 2 ? "bg-[#16A34A]" : i === 2 ? "bg-[#14532D]" : "bg-[#E2E8F0]"
          }`}
        />
      ))}
    </div>
    <p className="mt-5 font-display text-lg font-bold leading-snug text-[#17201A] sm:text-xl">
      Solve 3x² − 5x + 2 = 0
    </p>
    <div className="mt-4 flex flex-wrap items-center gap-3">
      <span className="inline-flex items-center gap-2 rounded-xl border-2 border-[#16A34A] bg-[#16A34A]/8 px-3.5 py-2 text-sm font-bold text-[#14532D]">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#16A34A] text-[10px] font-extrabold text-white">
          A
        </span>
        x = 1, x = 2/3
      </span>
      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#16A34A] sm:text-sm">
        <CheckCircle2 className="h-4.5 w-4.5" />
        Correct — derivation shown instantly
      </span>
    </div>
    <p className="mt-5 inline-flex items-center gap-2 rounded-lg border border-[#FACC15]/50 bg-[#FEF9C3] px-3.5 py-1.5 text-xs font-semibold text-[#17201A] sm:text-sm">
      <Target className="h-4 w-4 shrink-0 text-[#B45309]" />
      Weak area — quadratic word problems need more practice
    </p>
    <CropFade />
  </div>
);

/* ---------- LEARN state — video glimpse ---------- */
const LearnVisual = () => (
  <div data-testid="how-works-visual-learn" className={frameClass}>
    <div className="p-6 sm:p-8">
      <p className="text-xs font-bold text-[#647067] sm:text-sm">
        Class 12 Physics <span className="mx-1 text-[#94A39B]">·</span>{" "}
        <span className="text-[#17201A]">Ray Optics</span>
      </p>
      <div className="relative mt-4 aspect-[16/8] overflow-hidden rounded-2xl bg-[#17201A]">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 30% 30%, rgba(34,197,94,0.35), transparent 55%), radial-gradient(circle at 75% 70%, rgba(250,204,21,0.18), transparent 50%)",
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 shadow-lg sm:h-16 sm:w-16">
            <Play className="ml-1 h-6 w-6 fill-[#16A34A] text-[#16A34A] sm:h-7 sm:w-7" />
          </span>
        </div>
        <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 px-4 pb-3.5">
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/25">
            <div className="h-full w-1/3 rounded-full bg-[#22C55E]" />
          </div>
          <span className="text-[11px] font-bold text-white/80">04:42 / 14:20</span>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-display text-base font-bold text-[#17201A] sm:text-lg">
            Derivation of Lens Maker's Formula
          </p>
          <p className="text-xs font-semibold text-[#647067] sm:text-sm">
            14:20 · Full-chapter lesson · Board-focused
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#16A34A] px-4 py-2.5 text-sm font-bold text-white">
          Practice this topic
          <ChevronRight className="h-4 w-4" />
        </span>
      </div>
    </div>
    <CropFade />
  </div>
);

/* ---------- AI state — conversation glimpse ---------- */
const AiVisual = () => (
  <div data-testid="how-works-visual-ai" className={`${frameClass} p-6 sm:p-8`}>
    <div className="flex items-center gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#16A34A]/10">
        <Sparkles className="h-5 w-5 text-[#16A34A]" />
      </span>
      <p className="text-sm font-extrabold text-[#17201A] sm:text-base">
        Ask Abhyas
      </p>
      <span className="ml-auto rounded-full border border-[#FACC15]/50 bg-[#FEF9C3] px-2.5 py-1 text-[11px] font-bold text-[#14532D] sm:text-xs">
        Class 12 Physics · Ray Optics
      </span>
    </div>
    <div className="mt-5 flex justify-end">
      <div className="max-w-[85%] rounded-2xl rounded-br-md bg-[#17201A] px-4 py-3 text-sm font-semibold leading-relaxed text-white sm:text-[15px]">
        Why does light bend towards the normal in a denser medium?
      </div>
    </div>
    <div className="mt-4 max-w-[94%] rounded-2xl rounded-bl-md border border-[#16A34A]/15 bg-[#F8FAF7] px-4 py-4 sm:px-5">
      <p className="text-sm font-semibold leading-relaxed text-[#17201A] sm:text-[15px]">
        Light slows down in a denser medium — Snell's Law describes the bend:
      </p>
      <p className="mt-3 w-fit rounded-xl bg-white px-4 py-2 font-display text-base font-bold text-[#14532D] sm:text-lg">
        n₁ sin θ₁ = n₂ sin θ₂
      </p>
      <p className="mt-3 text-sm leading-relaxed text-[#647067] sm:text-[15px]">
        When n₂ &gt; n₁, θ₂ must shrink — so the ray bends{" "}
        <b className="text-[#17201A]">towards the normal</b>.
      </p>
    </div>
    <CropFade />
  </div>
);

const STATE_CONTENT = {
  practice: {
    headline: "Turn every study session into meaningful practice.",
    copy: "Learn a concept, practice it, evaluate your understanding, and keep improving — all in one preparation flow.",
    visual: <PracticeVisual />,
  },
  learn: {
    headline: "Understand before you move on.",
    copy: "Find chapter explanations and focused learning resources, then move straight into practice when you're ready.",
    visual: <LearnVisual />,
  },
  ai: {
    headline: "Your questions don't have to wait.",
    copy: "Ask questions, understand difficult concepts, and get guidance based on what you're actually preparing for.",
    visual: <AiVisual />,
  },
};

const ProductPreviewSection = () => {
  const [active, setActive] = useState("practice");
  const content = STATE_CONTENT[active];

  return (
    <section
      id="how-it-works"
      data-testid="product-preview-section"
      className="relative scroll-mt-20 overflow-hidden px-4 py-20 text-center sm:px-6 sm:py-28 lg:py-32"
    >
      <div className="mx-auto max-w-6xl">
        <span
          data-testid="how-works-eyebrow"
          className="mb-3.5 inline-flex items-center gap-2 rounded-full bg-[#14532D]/5 px-3 py-1 text-[13px] font-bold uppercase tracking-wider text-[#14532D] sm:text-[14px]"
        >
          <span className="pulse-dot inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#16A34A]" />
          How Abhyas Works
        </span>
        <h2
          data-testid="how-works-headline"
          className="mx-auto max-w-3xl font-display text-[36px] font-bold leading-[1.1] tracking-tight text-[#17201A] sm:text-[52px] lg:text-[64px]"
        >
          See how <span className="text-[#16A34A]">Abhyas</span> works.
        </h2>
        <p className="mx-auto mt-5 max-w-2xl font-body text-[17px] leading-[1.6] text-[#647067] sm:text-[19px] lg:text-[20px]">
          From choosing what to study to understanding where you need to
          improve, Abhyas keeps your preparation connected.
        </p>

        {/* Refined typographic selector — active underline, one row, no scrollbar */}
        <div
          data-testid="how-works-selector"
          className="mx-auto mt-10 flex items-center justify-center gap-6 border-b border-[#E2E8F0] sm:mt-12 sm:gap-10"
        >
          {STATES.map((s) => (
            <button
              key={s.id}
              type="button"
              data-testid={s.testId}
              data-active={active === s.id}
              aria-pressed={active === s.id}
              onClick={() => setActive(s.id)}
              className={`relative whitespace-nowrap pb-2.5 text-base transition-colors duration-200 sm:text-lg ${
                active === s.id
                  ? "font-bold text-[#14532D] after:absolute after:-bottom-px after:left-0 after:right-0 after:h-[2.5px] after:rounded-full after:bg-[#14532D]"
                  : "font-semibold text-[#647067] hover:text-[#17201A]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Active state — headline, copy, glimpse */}
        <div key={active} className="mt-12 animate-[fadeSlideIn_.3s_ease-out] sm:mt-14">
          <h3 data-testid="how-works-state-headline" className={stateHeadline}>
            {content.headline}
          </h3>
          <p className={stateCopy}>{content.copy}</p>
          {content.visual}
        </div>
      </div>
    </section>
  );
};

export default ProductPreviewSection;
